import _defineProperty from "@babel/runtime/helpers/defineProperty";
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { easeOut, prefersReducedMotion } from '@atlaskit/motion';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { UNSAFE_media } from '@atlaskit/primitives/responsive';
import { BANNER_HEIGHT, LEFT_PANEL_WIDTH, LEFT_SIDEBAR_FLYOUT_WIDTH, LEFT_SIDEBAR_WIDTH, MAX_MOBILE_SIDEBAR_FLYOUT_WIDTH, MOBILE_COLLAPSED_LEFT_SIDEBAR_WIDTH, TOP_NAVIGATION_HEIGHT, TRANSITION_DURATION } from '../../../common/constants';
import { useIsSidebarDragging } from '../../../common/hooks';
// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
var prefersReducedMotionStyles = css(prefersReducedMotion());

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- With a feature flag, this does not apply
var mobileStyles = getBooleanFF('platform.design-system-team.responsive-page-layout-left-sidebar_p8r7g') ? css(_defineProperty({}, UNSAFE_media.below.sm, {
  width: "".concat(MOBILE_COLLAPSED_LEFT_SIDEBAR_WIDTH, "px"),
  position: 'fixed',
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
  top: "calc(".concat(BANNER_HEIGHT, " + ").concat(TOP_NAVIGATION_HEIGHT, ")"),
  bottom: 0,
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
  left: "".concat(LEFT_PANEL_WIDTH),
  transition: "width ".concat(TRANSITION_DURATION, "ms ").concat(easeOut, " 0s")
})) : undefined;

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- With a feature flag, this does not apply
var mobileInnerFlyoutStyles = getBooleanFF('platform.design-system-team.responsive-page-layout-left-sidebar_p8r7g') ? css(_defineProperty({}, UNSAFE_media.below.sm, {
  width: "min(90vw, ".concat(MAX_MOBILE_SIDEBAR_FLYOUT_WIDTH, "px)"),
  maxWidth: MAX_MOBILE_SIDEBAR_FLYOUT_WIDTH,
  transition: "width ".concat(TRANSITION_DURATION, "ms ").concat(easeOut, " 0s, box-shadow ").concat(TRANSITION_DURATION, "ms ").concat(easeOut, " 0s")
})) : undefined;

/**
 * This inner wrapper is required to allow the sidebar to be `position: fixed`.
 *
 * If we were to apply `position: fixed` to the outer wrapper, it will be popped
 * out of its flex container and Main would stretch to occupy all the space.
 */
var fixedInnerStyles = css({
  width: "".concat(LEFT_SIDEBAR_WIDTH),
  position: 'fixed',
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
  top: "calc(".concat(BANNER_HEIGHT, " + ").concat(TOP_NAVIGATION_HEIGHT, ")"),
  bottom: 0,
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
  left: "".concat(LEFT_PANEL_WIDTH),
  transition: "width ".concat(TRANSITION_DURATION, "ms ").concat(easeOut, " 0s")
});
var fixedInnerFlyoutStyles = css({
  width: LEFT_SIDEBAR_FLYOUT_WIDTH
});

/**
 * Static in the sense of `position: static`.
 *
 * It will expand the page height to fit its content.
 */
var staticInnerStyles = css({
  height: '100%'
});
var draggingStyles = css({
  cursor: 'ew-resize',
  // Make sure drag to resize does not animate as the user drags
  transition: 'none'
});
var LeftSidebarInner = function LeftSidebarInner(_ref) {
  var children = _ref.children,
    _ref$isFixed = _ref.isFixed,
    isFixed = _ref$isFixed === void 0 ? false : _ref$isFixed,
    _ref$isFlyoutOpen = _ref.isFlyoutOpen,
    isFlyoutOpen = _ref$isFlyoutOpen === void 0 ? false : _ref$isFlyoutOpen;
  var isDragging = useIsSidebarDragging();
  return jsx("div", {
    css: [
    // mobile breakpoint styles
    mobileStyles, isFlyoutOpen && mobileInnerFlyoutStyles,
    // generic styles
    !isFixed && staticInnerStyles, isFixed && fixedInnerStyles, isFixed && isFlyoutOpen && fixedInnerFlyoutStyles, isDragging && draggingStyles, prefersReducedMotionStyles]
  }, children);
};

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export default LeftSidebarInner;